// Clean single-component implementation (previous corrupted nested code removed)
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import AuthContext from "../AuthContext";
import { useNotification } from "../NotificationContext";
import { useReminder } from "../ReminderContext";

const AppointmentsPage = () => {
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();

  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locks, setLocks] = useState({}); // booking_id -> { booking_id, locked_by, locked_by_username, expires_at }
  const lockHeartbeatRef = useRef(null);
  const lockedEditingIdRef = useRef(null);

  const initialBookingForm = { id: null, customer_name: "", customer_phone: "", booking_datetime: "", service_id: "", employee_id: "", notes: "", status: "-" };
  const [bookingForm, setBookingForm] = useState(initialBookingForm);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isEditingBooking, setIsEditingBooking] = useState(false);

  const { settings: reminderSettings, updateSettings, triggerReminder } = useReminder();

  const fmtLocalDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const [selectedDate, setSelectedDate] = useState(fmtLocalDate(new Date())); // single date mode
  const [useRange, setUseRange] = useState(false);
  const [dateFrom, setDateFrom] = useState(fmtLocalDate(new Date()));
  const [dateTo, setDateTo] = useState(fmtLocalDate(new Date()));
  const [rangeError, setRangeError] = useState("");
  const [rangeDays, setRangeDays] = useState(0);
  const applyPreset = (preset) => {
    const today = new Date();
    const fmt = d => fmtLocalDate(d);
    if (preset === 'today') {
      setUseRange(false);
      setSelectedDate(fmt(today));
      return;
    }
    if (preset === 'last7') {
      const start = new Date(today.getTime() - 6 * 86400000);
      setUseRange(true);
      setDateFrom(fmt(start));
      setDateTo(fmt(today));
      return;
    }
    if (preset === 'week') {
      // สัปดาห์นี้ (จันทร์-อาทิตย์)
      const day = today.getDay(); // 0=Sun
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const monday = new Date(today.getTime() + mondayOffset * 86400000);
      const sunday = new Date(monday.getTime() + 6 * 86400000);
      setUseRange(true);
      setDateFrom(fmt(monday));
      setDateTo(fmt(sunday));
      return;
    }
    if (preset === 'month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0); // last day
      setUseRange(true);
      setDateFrom(fmt(first));
      setDateTo(fmt(last));
      return;
    }
    if (preset === 'reset') {
      setUseRange(false);
      setSelectedDate(fmt(today));
      setRangeError('');
      setRangeDays(0);
    }
  };
  // Detect currently matched preset for button highlighting
  const activePreset = useMemo(() => {
    const todayStr = fmtLocalDate(new Date());
    if (!useRange) {
      if (selectedDate === todayStr) return 'today';
      return null;
    }
    if (rangeError || !dateFrom || !dateTo) return null;
    try {
      const dFrom = new Date(dateFrom);
      const dTo = new Date(dateTo);
      const today = new Date();
      const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
      // month
      const firstMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      if (sameDay(dFrom, firstMonth) && sameDay(dTo, lastMonth)) return 'month';
      // week (Mon-Sun)
      const day = today.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const monday = new Date(today.getTime() + mondayOffset * 86400000);
      const sunday = new Date(monday.getTime() + 6 * 86400000);
      if (sameDay(dFrom, monday) && sameDay(dTo, sunday)) return 'week';
      // last7 (today and previous 6 days)
      const last7Start = new Date(today.getTime() - 6 * 86400000);
      if (sameDay(dFrom, last7Start) && sameDay(dTo, today)) return 'last7';
    } catch { }
    return null;
  }, [useRange, selectedDate, dateFrom, dateTo, rangeError]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const pageFont = { fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif", fontSize: "0.95rem" };

  // --- Mini Calendar (week) state ---
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setDate(d.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const [calendarEvents, setCalendarEvents] = useState({}); // { 'YYYY-MM-DD': [appt,...] }
  // Generate date key from Date object using local timezone (not UTC)
  const dateKey = (d) => {
    if (d instanceof Date) {
      return fmtLocalDate(d);
    }
    return fmtLocalDate(new Date(d));
  };
  const weekDays = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) { const dd = new Date(calendarWeekStart); dd.setDate(dd.getDate() + i); arr.push(dd); }
    return arr;
  }, [calendarWeekStart]);
  const isToday = (d) => {
    const t = new Date();
    return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
  };
  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6; // Sun or Sat
  const gotoWeek = (deltaWeeks) => {
    const next = new Date(calendarWeekStart);
    next.setDate(next.getDate() + deltaWeeks * 7);
    setCalendarWeekStart(next);
  };
  const resetWeekToday = () => {
    const d = new Date();
    const day = d.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setDate(d.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    setCalendarWeekStart(monday);
  };
  const fetchCalendarWeek = useCallback(async (start) => {
    try {
      const s = new Date(start);
      const e = new Date(start);
      e.setDate(s.getDate() + 6);
      const resp = await api.get('/bookings', { params: { date_from: fmtLocalDate(s), date_to: fmtLocalDate(e) } });
      const map = {};
      (resp.data || []).forEach(ev => {
        try {
          const when = ev.appointment_date || ev.booking_datetime;
          const d = new Date(when);
          const k = dateKey(d);
          if (!map[k]) map[k] = [];
          map[k].push(ev);
        } catch { }
      });
      // sort by time in each day
      Object.keys(map).forEach(k => map[k].sort((a, b) => new Date(a.appointment_date || a.booking_datetime) - new Date(b.appointment_date || b.booking_datetime)));
      setCalendarEvents(map);
    } catch {
      setCalendarEvents({});
    }
  }, []);
  // refresh mini calendar when weekStart changes
  useEffect(() => { fetchCalendarWeek(calendarWeekStart); }, [calendarWeekStart, fetchCalendarWeek]);
  // keep week in sync with selectedDate (when user changes filters)
  useEffect(() => {
    try {
      const base = useRange ? dateFrom : selectedDate;
      const parts = (base || '').split('-');
      const d = parts.length === 3 ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])) : new Date();
      if (Number.isNaN(d.getTime())) return;
      const day = d.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const monday = new Date(d);
      monday.setDate(d.getDate() + mondayOffset);
      monday.setHours(0, 0, 0, 0);
      const currKey = dateKey(calendarWeekStart);
      const nextKey = dateKey(monday);
      if (currKey !== nextKey) setCalendarWeekStart(monday);
    } catch { }
  }, [selectedDate, useRange, dateFrom]);

  // Helpers for calendar event rendering
  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }); } catch { return '--:--'; }
  };
  const statusDot = (status) => {
    const map = { pending: '#6c757d', confirmed: '#d4af37', completed: '#198754', canceled: '#dc3545', '-': '#6c757d' };
    const bg = map[status] || '#6c757d';
    return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: bg, marginRight: 6 }} />;
  };
  const openAddBookingAt = (d) => {
    try {
      const x = new Date(d);
      // round to next 30 minutes for convenience
      const m = x.getMinutes();
      const add = m === 0 ? 0 : (m <= 30 ? (30 - m) : (60 - m));
      x.setMinutes(m + add, 0, 0);
      const pad = (n) => String(n).padStart(2, '0');
      const v = `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
      setBookingForm(p => ({ ...p, id: null, customer_name: '', customer_phone: '', booking_datetime: v, service_id: '', employee_id: '', notes: '', status: '-' }));
      setIsEditingBooking(false);
      setIsBookingModalOpen(true);
    } catch { }
  };

  const toIsoSeconds = v => (v && v.length === 16 ? `${v}:00` : v);
  const toIntOrNull = v => (v === "" || v == null ? null : Number(v));
  const requestNotificationPermission = () => { if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission(); };
  const formatDateForInput = iso => { try { const d = new Date(iso); const pad = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; } catch { return ""; } };
  const formatDateTime = iso => new Date(iso).toLocaleString('th-TH', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  const getTimeRemaining = iso => { const now = new Date(); const t = new Date(iso); const diffMs = t - now; const diffMins = Math.floor(diffMs / 60000); if (diffMs < 0) return { text: "เลยเวลาแล้ว", class: "text-danger fw-bold" }; if (diffMins < 60) return { text: `${diffMins} นาที`, class: diffMins <= 15 ? "text-danger fw-bold" : "text-warning" }; const h = Math.floor(diffMins / 60), m = diffMins % 60; return { text: `${h} ชม. ${m} นาที`, class: h < 1 ? "text-warning" : "text-success" }; };
  const getStatusBadge = st => { const map = { "-": { t: "รอกำหนด", c: "badge bg-secondary" }, pending: { t: "นัดหมาย(ไม่มัดจำ)", c: "badge bg-secondary" }, confirmed: { t: "นัดหมาย(มัดจำ)", c: "badge text-white", style: { backgroundColor: '#d4af37' } }, completed: { t: "เข้ารับบริการแล้ว", c: "badge bg-success" }, canceled: { t: "ยกเลิกนัด", c: "badge bg-danger" } }; const i = map[st] || { t: st, c: "badge bg-light text-dark" }; return <span className={i.c} style={i.style}>{i.t}</span>; };

  const isAdmin = user?.role === 'admin';
  const canManageBooking = b => !(b.status === 'completed' || b.status === 'canceled') || isAdmin;

  const loadData = async () => {
    try {
      setLoading(true);
      let r;
      let params = {};
      if (useRange) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      } else {
        // Use date_from and date_to with same date for single day mode
        params.date_from = selectedDate;
        params.date_to = selectedDate;
      }
      try { r = await api.get('/bookings', { params }); } catch { r = await api.get('/bookings'); }
      setBookings(r.data);

      const [srv, emp] = await Promise.all([api.get('/services'), api.get('/employees')]);
      const servicesRaw = (srv.data || [])
        .filter(s => s.is_active !== false && (s.item_type !== 'product')) // exclude products from booking services
        .map(s => ({ id: s.service_id, name: s.service_name, is_active: s.is_active, item_type: s.item_type }));
      const employeesRaw = (emp.data || [])
        .filter(e => e.is_active !== false)
        .map(e => ({ id: e.emp_id, name: e.emp_name, phone: e.phone, is_active: e.is_active }));
      setServices(servicesRaw);
      setEmployees(employeesRaw);
      // Fetch lock statuses for listed bookings
      try {
        const ids = (r.data || []).map(b => b.id).filter(Boolean);
        if (ids.length) {
          const respL = await api.get('/bookings/locks', { params: { ids: ids.join(',') } });
          const map = {};
          (respL.data || []).forEach(l => { map[l.booking_id] = l; });
          setLocks(map);
        } else {
          setLocks({});
        }
      } catch {
        // ignore lock fetch errors
      }
    } catch (e) {
      addNotification(e?.response?.data?.message || e.message || 'โหลดข้อมูลล้มเหลว', 'error');
    } finally {
      setLoading(false);
    }
  };
  // validate range and load
  useEffect(() => {
    if (useRange) {
      setRangeError("");
      if (!dateFrom || !dateTo) return;
      try {
        const dF = new Date(dateFrom);
        const dT = new Date(dateTo);
        if (dT < dF) {
          setRangeError('date_to ต้องไม่เล็กกว่า date_from');
          setRangeDays(0);
          return;
        }
        const spanDays = Math.floor((dT - dF) / 86400000) + 1;
        if (spanDays > 30) {
          setRangeError('ช่วงวันที่ต้องไม่เกิน 30 วัน');
          setRangeDays(0);
          return;
        }
        setRangeDays(spanDays);
      } catch {
        setRangeError('รูปแบบวันที่ผิดพลาด');
        setRangeDays(0);
        return;
      }
      loadData();
    } else {
      setRangeDays(0);
      loadData();
    }
    requestNotificationPermission();
  }, [useRange, selectedDate, dateFrom, dateTo]);
  useEffect(() => setCurrentPage(1), [statusFilter, timeFilter, searchText, selectedDate, useRange, dateFrom, dateTo, pageSize]);

  // Removed local scheduling; global ReminderProvider manages timeouts.

  const filteredBookings = useMemo(() => bookings.filter(b => { const statusOk = statusFilter === 'all' || b.status === statusFilter; const searchOk = !searchText || [b.customer_name, b.customer_phone, b.service_name, b.employee_name].filter(Boolean).some(v => v.toLowerCase?.().includes(searchText.toLowerCase()) || v.includes?.(searchText)); let timeOk = true; if (timeFilter !== 'all') { const tr = getTimeRemaining(b.booking_datetime); timeOk = timeFilter === 'upcoming' ? (tr.text.includes('นาที') && !tr.text.includes('เลยเวลา')) : tr.text.includes('เลยเวลา'); } return statusOk && searchOk && timeOk; }), [bookings, statusFilter, searchText, timeFilter]);
  const countsAll = useMemo(() => {
    return {
      pending: bookings.filter(b => b.status === 'pending').length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      completed: bookings.filter(b => b.status === 'completed').length,
      canceled: bookings.filter(b => b.status === 'canceled').length,
    };
  }, [bookings]);
  const countsVisible = useMemo(() => {
    return {
      pending: filteredBookings.filter(b => b.status === 'pending').length,
      confirmed: filteredBookings.filter(b => b.status === 'confirmed').length,
      completed: filteredBookings.filter(b => b.status === 'completed').length,
      canceled: filteredBookings.filter(b => b.status === 'canceled').length,
    };
  }, [filteredBookings]);
  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / pageSize));
  useEffect(() => setCurrentPage(p => Math.min(p, totalPages)), [totalPages]);
  const paginatedBookings = useMemo(() => filteredBookings.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize), [filteredBookings, currentPage, pageSize]);
  const startIndex = (currentPage - 1) * pageSize; const endIndex = Math.min(filteredBookings.length, startIndex + paginatedBookings.length);

  const stopHeartbeat = () => { if (lockHeartbeatRef.current) { clearInterval(lockHeartbeatRef.current); lockHeartbeatRef.current = null; } };
  const releaseLockIfHeld = async () => {
    const id = lockedEditingIdRef.current;
    if (id) {
      try { await api.delete(`/bookings/${id}/lock`); } catch { /* ignore */ }
      lockedEditingIdRef.current = null;
      stopHeartbeat();
    }
  };
  const openModal = () => setIsBookingModalOpen(true);
  const closeModal = async () => { await releaseLockIfHeld(); setIsBookingModalOpen(false); };

  const handleAddBooking = () => { setBookingForm(initialBookingForm); setIsEditingBooking(false); openModal(); };
  const handleEditBooking = async (b) => {
    // Try acquire/refresh lock before editing
    try {
      const resp = await api.post(`/bookings/${b.id}/lock`);
      lockedEditingIdRef.current = b.id;
      // Heartbeat to refresh lock every 90s
      stopHeartbeat();
      lockHeartbeatRef.current = setInterval(async () => { try { await api.post(`/bookings/${b.id}/lock`); } catch {/* ignore */ } }, 90000);
      // Update local lock cache to reflect ownership
      setLocks(prev => ({
        ...prev, [b.id]: {
          booking_id: b.id,
          locked_by: resp.data?.locked_by ?? user?.id,
          locked_by_username: resp.data?.locked_by_username ?? user?.username,
          expires_at: resp.data?.expires_at
        }
      }));
      setBookingForm({ id: b.id, customer_name: b.customer_name, customer_phone: b.customer_phone || "", booking_datetime: formatDateForInput(b.booking_datetime), service_id: b.service_id || "", employee_id: b.employee_id || "", notes: b.notes || "", status: b.status });
      setIsEditingBooking(true);
      openModal();
    } catch (e) {
      const msg = e?.response?.data?.message || 'ไม่สามารถแก้ไขได้ ขณะนี้มีผู้อื่นกำลังแก้ไขอยู่';
      addNotification(msg, 'warning');
      // refresh lock info for this booking
      try {
        const respL = await api.get('/bookings/locks', { params: { ids: String(b.id) } });
        const map = {};
        (respL.data || []).forEach(l => { map[l.booking_id] = l; });
        setLocks(prev => ({ ...prev, ...map }));
      } catch { }
    }
  };
  const handleSaveBooking = async () => {
    try {
      const basePayload = {
        customer_name: bookingForm.customer_name,
        customer_phone: bookingForm.customer_phone,
        booking_datetime: toIsoSeconds(bookingForm.booking_datetime),
        service_id: toIntOrNull(bookingForm.service_id),
        employee_id: toIntOrNull(bookingForm.employee_id),
        notes: bookingForm.notes
      };
      if (isEditingBooking) {
        // Allow updating status when editing
        await api.put(`/bookings/${bookingForm.id}`, { ...basePayload, status: bookingForm.status });
        addNotification('แก้ไขนัดหมายสำเร็จ', 'success');
      } else {
        // Do not send status on creation; backend will set default
        await api.post('/bookings', basePayload);
        addNotification('เพิ่มนัดหมายสำเร็จ', 'success');
      }
      await releaseLockIfHeld();
      setIsBookingModalOpen(false);
      loadData();
    } catch (e) {
      addNotification(e?.response?.data?.message || e.message || 'บันทึกผิดพลาด', 'error');
    }
  };
  const handleDeleteBooking = async id => { if (!window.confirm('ต้องการลบนัดหมายนี้หรือไม่?')) return; try { await api.delete(`/bookings/${id}`); addNotification('ลบนัดหมายสำเร็จ', 'success'); loadData(); } catch (e) { addNotification(e?.response?.data?.message || e.message || 'ลบผิดพลาด', 'error'); } };
  const handleStatusChange = async (id, status) => { try { await api.put(`/bookings/${id}`, { status }); addNotification('อัปเดตสถานะเรียบร้อยแล้ว', 'success'); loadData(); } catch (e) { addNotification(e?.response?.data?.message || e.message || 'อัปเดตสถานะผิดพลาด', 'error'); } };

  // release lock when unmounting this page
  useEffect(() => { return () => { releaseLockIfHeld(); }; }, []);

  return (
    <div className="dashboard-page container-fluid py-4">
      {/* Luxury Hero Header */}
      <div className="dashboard-hero mb-4">
        <div className="d-flex align-items-center gap-3">
          <i className="bi bi-calendar-check" style={{ fontSize: '28px', color: '#d4af37' }}></i>
          <div>
            <div className="dashboard-hero-title mb-0">จัดการนัดหมาย</div>
            <div className="dashboard-hero-subtitle">พบ {filteredBookings.length} นัดหมาย{filteredBookings.length !== bookings.length && ` (จากทั้งหมด ${bookings.length})`}</div>
          </div>
        </div>
        <button className="btn btn-ks-primary" style={{ color: '#ffffff' }} onClick={handleAddBooking}><i className="bi bi-plus-lg me-1" />เพิ่มนัดหมาย</button>
      </div>

      <div className="row"><div className="col-12"><div className="card shadow-sm">
        <div className="card-header bg-light border-bottom">
          <div className="row align-items-center">
            <div className="col-md-6"><div className="d-flex align-items-center"><i className="bi bi-bell me-2 text-warning"></i><span className="fw-bold">ระบบเตือนนัดหมาย</span></div></div>
            <div className="col-md-6"><div className="row g-2 align-items-center">
              <div className="col-auto">
                <div className="form-check" title={!isAdmin ? 'แก้ไขได้เฉพาะผู้ดูแลระบบ' : ''}>
                  <input className="form-check-input" type="checkbox" id="reminderEnabled" checked={reminderSettings.enabled} disabled={!isAdmin}
                    onChange={e => { if (!isAdmin) return; updateSettings({ enabled: e.target.checked }); if (e.target.checked) requestNotificationPermission(); }} />
                  <label className="form-check-label" htmlFor="reminderEnabled">เปิดใช้งาน</label>
                </div>
              </div>
              <div className="col-auto">
                <select className="form-select form-select-sm" style={{ width: '160px' }} value={reminderSettings.minutesBefore} disabled={!reminderSettings.enabled || !isAdmin}
                  onChange={e => { if (!isAdmin) return; updateSettings({ minutesBefore: parseInt(e.target.value) }) }}>
                  <option value="5">ล่วงหน้า 5 นาที</option>
                  <option value="10">ล่วงหน้า 10 นาที</option>
                  <option value="15">ล่วงหน้า 15 นาที</option>
                  <option value="30">ล่วงหน้า 30 นาที</option>
                  <option value="60">ล่วงหน้า 1 ชั่วโมง</option>
                </select>
              </div>
              <div className="col-auto"><small className="text-muted">ก่อนนัดหมาย</small></div>
              {!isAdmin && <div className="col-12"><small className="text-muted">การตั้งค่านี้กำหนดโดยผู้ดูแลระบบ (Global)</small></div>}
            </div></div>
          </div>
        </div>
        <div className="card-body">
          {/* Mini Calendar (compact week view) */}
          <div className="card mb-3" style={{ border: '1px solid #eee' }}>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => gotoWeek(-1)} title="สัปดาห์ก่อน"><i className="bi bi-chevron-left" /></button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={resetWeekToday} title="สัปดาห์นี้">วันนี้</button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => gotoWeek(1)} title="สัปดาห์ถัดไป"><i className="bi bi-chevron-right" /></button>
                </div>
                <div className="small text-muted">
                  สัปดาห์: {weekDays[0]?.toLocaleDateString('th-TH', { day: '2-digit', month: 'short' })} - {weekDays[6]?.toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <div className="table-responsive">
                <div className="mt-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', fontSize: '0.9rem', minWidth: '1000px' }}>
                  {weekDays.map((d) => {
                    const k = dateKey(d);
                    const evs = calendarEvents[k] || [];
                    const isSelected = !useRange && selectedDate === k;
                    const today = isToday(d);
                    const wknd = isWeekend(d);
                    const dow = d.getDay();
                    const dowColor = (g) => {
                      switch (g) {
                        case 0: return '#dc3545'; // Sun - red
                        case 1: return '#ffc107'; // Mon - yellow
                        case 2: return '#e83e8c'; // Tue - pink
                        case 3: return '#198754'; // Wed - green
                        case 4: return '#fd7e14'; // Thu - orange
                        case 5: return '#0d6efd'; // Fri - blue
                        case 6: return '#6f42c1'; // Sat - purple
                        default: return undefined;
                      }
                    };
                    return (
                      <div key={k}>
                        <div
                          className={`border rounded p-2 h-100 ${isSelected ? 'border-primary' : ''}`}
                          style={{
                            minHeight: '140px',
                            cursor: 'pointer',
                            background: wknd ? '#fcfcfd' : '#fff',
                            boxShadow: today ? 'inset 0 0 0 2px #0d6efd22' : undefined
                          }}
                          onClick={() => { setUseRange(false); setSelectedDate(k); }}
                          onDoubleClick={() => openAddBookingAt(new Date(d.getFullYear(), d.getMonth(), d.getDate(), new Date().getHours(), new Date().getMinutes()))}
                          title={today ? 'วันนี้ — คลิกเพื่อเลือก / ดับเบิลคลิกเพื่อเพิ่มนัด' : 'คลิกเพื่อเลือก / ดับเบิลคลิกเพื่อเพิ่มนัด'}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-1">
                              <strong style={{ color: dowColor(dow) }}>{d.toLocaleDateString('th-TH', { weekday: 'short' })}</strong>
                              <span className="badge rounded-pill bg-light text-dark border" title={`รวม ${evs.length} นัด`}>{evs.length}</span>
                            </div>
                            <span className={`badge ${today ? 'bg-primary' : 'bg-secondary'}`} style={{ borderRadius: 16, minWidth: 28 }}>{d.getDate()}</span>
                          </div>
                          <div style={{ maxHeight: 100, overflowY: 'auto', paddingRight: 2 }}>
                            {evs.slice(0, 4).map(e => (
                              <div key={e.id} className="d-flex align-items-center text-truncate mb-1" title={`${fmtTime(e.appointment_date || e.booking_datetime)} • ${e.service_name || '-'} • ${e.employee_name || '-'}`}>
                                {statusDot(e.status)}
                                <small className="text-nowrap me-1" style={{ fontWeight: 600 }}>{fmtTime(e.appointment_date || e.booking_datetime)}</small>
                                <span className="text-muted text-truncate">{e.customer_name}</span>
                              </div>
                            ))}
                            {evs.length > 4 && (
                              <div className="text-muted small">+{evs.length - 4} เพิ่มเติม</div>
                            )}
                            {evs.length === 0 && (
                              <div className="text-muted small">ว่าง</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-3"><div className="card bg-light border-0"><div className="card-body text-center p-2" title={`ทั้งหมด: ${countsAll.pending}`}><div className="h5 mb-0" style={{ color: '#fd7e14' }}>{countsVisible.pending}</div><small className="text-muted">นัดหมาย(ไม่มัดจำ)</small></div></div></div>
            <div className="col-6 col-md-3"><div className="card bg-light border-0"><div className="card-body text-center p-2" title={`ทั้งหมด: ${countsAll.confirmed}`}><div className="h5 mb-0" style={{ color: "#d4af37" }}>{countsVisible.confirmed}</div><small className="text-muted">นัดหมาย(มัดจำ)</small></div></div></div>
            <div className="col-6 col-md-3"><div className="card bg-light border-0"><div className="card-body text-center p-2" title={`ทั้งหมด: ${countsAll.completed}`}><div className="h5 mb-0 text-success">{countsVisible.completed}</div><small className="text-muted">เข้ารับบริการแล้ว</small></div></div></div>
            <div className="col-6 col-md-3"><div className="card bg-light border-0"><div className="card-body text-center p-2" title={`ทั้งหมด: ${countsAll.canceled}`}><div className="h5 mb-0 text-danger">{countsVisible.canceled}</div><small className="text-muted">ยกเลิกนัด</small></div></div></div>
          </div>
          <div className="row g-2 align-items-start mb-2">
            <div className="col-12">
              <label className="form-label mb-1">ช่วงเวลา</label>
              <div className="bg-white border rounded p-2 d-flex flex-wrap align-items-center gap-2 w-100" style={{ boxShadow: 'inset 0 0 3px rgba(0,0,0,0.05)' }}>
                <div className="btn-group btn-group-sm" role="group" aria-label="date mode">
                  <button type="button" className={`btn ${!useRange ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setUseRange(false)}>วันเดียว</button>
                  <button type="button" className={`btn ${useRange ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setUseRange(true)}>ช่วงวันที่</button>
                </div>
                <div className="btn-group btn-group-sm" role="group" aria-label="quick presets">
                  <button type="button" className={`btn ${activePreset === 'today' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => applyPreset('today')} title="วันนี้">วันนี้</button>
                  <button type="button" className={`btn ${activePreset === 'last7' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => applyPreset('last7')} title="7 วันล่าสุด">7 วัน</button>
                  <button type="button" className={`btn ${activePreset === 'week' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => applyPreset('week')} title="สัปดาห์นี้">สัปดาห์</button>
                  <button type="button" className={`btn ${activePreset === 'month' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => applyPreset('month')} title="เดือนนี้">เดือน</button>
                  <button type="button" className="btn btn-outline-danger" onClick={() => applyPreset('reset')} title="รีเซ็ต">รีเซ็ต</button>
                </div>
                {/* Animated horizontal expansion inputs */}
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: '140px' }}
                  value={useRange ? dateFrom : selectedDate}
                  onChange={e => useRange ? setDateFrom(e.target.value) : setSelectedDate(e.target.value)}
                />
                <span
                  className="text-muted"
                  style={{ opacity: useRange ? 1 : 0, width: useRange ? 'auto' : '0px', transition: 'opacity .25s,width .25s', overflow: 'hidden' }}
                >→</span>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  disabled={!useRange}
                  style={{
                    width: useRange ? '140px' : '0px',
                    padding: useRange ? undefined : 0,
                    marginLeft: useRange ? '0.25rem' : 0,
                    border: useRange ? undefined : 'none',
                    opacity: useRange ? 1 : 0,
                    transition: 'width .25s ease, opacity .25s ease, margin .25s ease',
                    overflow: 'hidden'
                  }}
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                />
                {useRange && !rangeError && rangeDays > 0 && (
                  <small className="text-success ms-2">รวม {rangeDays} วัน</small>
                )}
                {useRange && rangeError && (
                  <small className="text-danger ms-2">{rangeError}</small>
                )}
              </div>
            </div>
          </div>
          <div className="row g-2 mb-3">
            <div className="col-md-2">
              <label className="form-label mb-1">สถานะ</label>
              <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}><option value="all">ทั้งหมด</option><option value="pending">นัดหมาย(ไม่มัดจำ)</option><option value="confirmed">นัดหมาย(มัดจำ)</option><option value="completed">เข้ารับบริการแล้ว</option><option value="canceled">ยกเลิกนัด</option></select>
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1">เวลา</label>
              <select className="form-select form-select-sm" value={timeFilter} onChange={e => setTimeFilter(e.target.value)}><option value="all">ทั้งหมด</option><option value="upcoming">ใกล้ถึงเวลา (≤1ชม.)</option><option value="overdue">เลยเวลาแล้ว</option></select>
            </div>
            <div className="col-md-6">
              <label className="form-label mb-1">ค้นหา</label>
              <input type="text" className="form-control form-control-sm" placeholder="ค้นหาชื่อลูกค้า เบอร์โทร บริการ หรือพนักงาน..." value={searchText} onChange={e => setSearchText(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1">แถว/หน้า</label>
              <select className="form-select form-select-sm" value={pageSize} onChange={e => setPageSize(parseInt(e.target.value))}><option value={5}>5</option><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option></select>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
          ) : (
            <div className="table-responsive" style={{ minHeight: '420px' }}>
              <table className="table table-sm table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '12%' }}>วันและเวลา</th>
                    <th style={{ width: '10%' }}>เหลือเวลา</th>
                    <th style={{ width: '18%' }}>ลูกค้า</th>
                    <th style={{ width: '10%' }}>บริการ</th>
                    <th style={{ width: '10%' }}>พนักงาน</th>
                    <th style={{ width: '10%' }}>สถานะ</th>
                    <th style={{ width: '15%' }}>หมายเหตุ</th>
                    <th style={{ width: '15%' }}>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBookings.map(b => {
                    const tr = getTimeRemaining(b.booking_datetime); const lockInfo = locks[b.id]; const myName = user?.username; const heldByMe = !!(lockInfo && ((lockInfo.locked_by_username && myName && lockInfo.locked_by_username === myName) || lockedEditingIdRef.current === b.id)); const lockedByOther = !!(lockInfo && !heldByMe); const allowed = canManageBooking(b) && !lockedByOther; return (
                      <tr key={b.id} onClick={() => { if (allowed) handleEditBooking(b); }} onKeyDown={e => { if (e.key === 'Enter' && allowed) handleEditBooking(b); }} tabIndex={0} style={{ cursor: allowed ? 'pointer' : 'default', opacity: lockedByOther ? 0.65 : undefined }} title={allowed ? 'คลิกเพื่อแก้ไขนัดหมาย' : (lockedByOther ? `กำลังแก้ไขโดย ${lockInfo?.locked_by_username || 'ผู้ใช้อื่น'}` : 'ไม่มีสิทธิ์จัดการรายการนี้')}>
                        <td className="fw-bold" style={{ color: "#d4af37" }}><i className="bi bi-clock me-1" />{formatDateTime(b.booking_datetime)}</td>
                        <td><span className={tr.class}><i className="bi bi-hourglass-split me-1" />{tr.text}</span></td>
                        <td><div><div className="fw-bold">{b.customer_name}</div>{b.customer_phone && <small className="text-muted"><i className="bi bi-telephone me-1" />{b.customer_phone}</small>}{lockedByOther && (<div><small className="text-danger"><i className="bi bi-lock me-1" />กำลังแก้ไขโดย {lockInfo?.locked_by_username || 'ผู้ใช้อื่น'}</small></div>)}</div></td>
                        <td><span className="badge bg-light text-dark">{b.service_name || '-'}</span></td>
                        <td><span className="badge bg-secondary text-white">{b.employee_name || '-'}</span></td>
                        <td>{getStatusBadge(b.status)}</td>
                        <td><small className="text-muted">{b.notes ? <span title={b.notes}>{b.notes.length > 20 ? `${b.notes.substring(0, 20)}...` : b.notes}</span> : '-'}</small></td>
                        <td style={{ width: '14%', minWidth: '180px' }} className="text-nowrap">
                          <div className="dropdown" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                            <button className={`btn btn-secondary btn-sm dropdown-toggle ${!allowed ? 'disabled' : ''}`} type="button" data-bs-toggle="dropdown" aria-expanded="false" title={lockedByOther ? `กำลังแก้ไขโดย ${lockInfo?.locked_by_username || 'ผู้ใช้อื่น'}` : "อัพเดทสถานะ / จัดการนัดหมาย"} disabled={!allowed} style={!allowed ? { opacity: 0.6, pointerEvents: 'none' } : undefined}><i className="bi bi-list-ul me-1" /> อัพเดทสถานะ</button>
                            <ul className="dropdown-menu" style={!allowed ? { pointerEvents: 'none', opacity: 0.8 } : undefined}>
                              {(b.status === 'pending' || b.status === 'confirmed') && (
                                <li><button className="dropdown-item" onClick={async e => { e.stopPropagation(); try { const resp = await api.post(`/reminders/force/${b.id}`); if (resp.data.booking) { triggerReminder(resp.data.booking); } addNotification('ส่งแจ้งเตือนแล้ว', 'success'); } catch (err) { addNotification(err?.response?.data?.message || 'ส่งแจ้งเตือนไม่สำเร็จ', 'error'); } }}><i className="bi bi-bell text-warning me-2" />แจ้งเตือนทันที (Global)</button></li>
                              )}
                              <li><hr className="dropdown-divider" /></li>
                              <li><button className="dropdown-item" onClick={e => { e.stopPropagation(); handleStatusChange(b.id, 'pending'); }} disabled={b.status === 'pending' || b.status === 'completed' || b.status === 'canceled'}><i className="bi bi-clock text-warning me-2" />นัดหมาย(ไม่มัดจำ)</button></li>
                              <li><button className="dropdown-item" onClick={e => { e.stopPropagation(); handleStatusChange(b.id, 'confirmed'); }} disabled={b.status === 'confirmed' || b.status === 'completed'}><i className="bi bi-check text-success me-2" />นัดหมาย(มัดจำ)</button></li>
                              <li><button className="dropdown-item" onClick={e => { e.stopPropagation(); handleStatusChange(b.id, 'completed'); }} disabled={b.status === 'completed'}><i className="bi bi-check2-all text-primary me-2" />เข้ารับบริการแล้ว</button></li>
                              <li><button className="dropdown-item" onClick={e => { e.stopPropagation(); handleStatusChange(b.id, 'canceled'); }} disabled={b.status === 'canceled'}><i className="bi bi-ban text-danger me-2" />ยกเลิกนัด</button></li>
                              <li><hr className="dropdown-divider" /></li>
                              <li><button className="dropdown-item" onClick={e => { e.stopPropagation(); handleEditBooking(b); }}><i className="bi bi-pencil me-2" />แก้ไขนัดหมาย</button></li>
                              <li><button className="dropdown-item text-danger" onClick={e => { e.stopPropagation(); handleDeleteBooking(b.id); }} disabled={!isAdmin || b.status === 'completed' || b.status === 'canceled'} title={!isAdmin ? 'ลบได้เฉพาะผู้ดูแลระบบ' : (b.status === 'completed' || b.status === 'canceled') ? 'รายการที่ปิดงานแล้วห้ามลบ' : ''}><i className="bi bi-trash me-2" />ลบนัดหมาย</button></li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">แสดง {filteredBookings.length === 0 ? 0 : startIndex + 1}-{endIndex} จาก {filteredBookings.length}</small>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}><i className="bi bi-chevron-left" /></button>
              <span className="btn btn-outline-secondary btn-sm disabled">หน้า {currentPage}/{totalPages}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}><i className="bi bi-chevron-right" /></button>
            </div>
          </div>
        </div>
      </div></div></div>

      {/* Global reminder modal moved to ReminderModal component in App.js */}

      {isBookingModalOpen && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog"><div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">{isEditingBooking ? 'แก้ไขนัดหมาย' : 'เพิ่มนัดหมาย'}</h5><button type="button" className="btn-close" onClick={closeModal} aria-label="Close"></button></div>
            <div className="modal-body">
              <div className="mb-3"><label className="form-label">ชื่อลูกค้า</label><input type="text" className="form-control" value={bookingForm.customer_name} onChange={e => setBookingForm(p => ({ ...p, customer_name: e.target.value }))} /></div>
              <div className="mb-3"><label className="form-label">เบอร์โทร</label><input type="text" className="form-control" value={bookingForm.customer_phone} onChange={e => setBookingForm(p => ({ ...p, customer_phone: e.target.value }))} /></div>
              <div className="mb-3"><label className="form-label">วันและเวลา</label><input type="datetime-local" className="form-control" value={bookingForm.booking_datetime} onChange={e => setBookingForm(p => ({ ...p, booking_datetime: e.target.value }))} /></div>
              <div className="row g-2">
                <div className="col-md-6"><label className="form-label">บริการ</label><select className="form-select" value={bookingForm.service_id} onChange={e => setBookingForm(p => ({ ...p, service_id: e.target.value }))}><option value="">-</option>{services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">พนักงาน</label><select className="form-select" value={bookingForm.employee_id} onChange={e => setBookingForm(p => ({ ...p, employee_id: e.target.value }))}><option value="">-</option>{employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}</select></div>
              </div>
              <div className="mt-2"><label className="form-label">หมายเหตุ</label><textarea className="form-control" rows="2" value={bookingForm.notes} onChange={e => setBookingForm(p => ({ ...p, notes: e.target.value }))} /></div>
              {isEditingBooking && (
                <div className="mt-2">
                  <label className="form-label">สถานะ</label>
                  <select
                    className="form-select"
                    value={bookingForm.status}
                    onChange={e => setBookingForm(p => ({ ...p, status: e.target.value }))}
                  >
                    <option value="-">รอกำหนด</option>
                    <option value="pending">นัดหมาย(ไม่มัดจำ)</option>
                    <option value="confirmed">นัดหมาย(มัดจำ)</option>
                    <option value="completed">เข้ารับบริการแล้ว</option>
                    <option value="canceled">ยกเลิกนัด</option>
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={closeModal}>ปิด</button><button type="button" className="btn btn-primary" onClick={handleSaveBooking}>บันทึก</button></div>
          </div></div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
